import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { aiAPI, promotionsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Wand2, Sparkles, Image, TrendingUp, Lock, 
  Copy, RefreshCw, Loader2, Crown, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const AITools = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Copy Generator State
  const [copyForm, setCopyForm] = useState({
    topic: '',
    tone: 'professional',
    category: ''
  });
  const [generatedCopy, setGeneratedCopy] = useState(null);
  
  // Optimizer State
  const [selectedPromo, setSelectedPromo] = useState('');
  const [optimization, setOptimization] = useState(null);
  
  // Image Generator State
  const [imageForm, setImageForm] = useState({
    prompt: '',
    style: 'professional'
  });
  const [generatedImage, setGeneratedImage] = useState(null);

  const isPro = user?.plan === 'pro' || user?.plan === 'power';
  const isPower = user?.plan === 'power';

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = async () => {
    try {
      const response = await promotionsAPI.getAll();
      setPromotions(response.data);
    } catch (error) {
      console.error('Failed to fetch promotions');
    }
  };

  const generateCopy = async () => {
    if (!isPro) {
      toast.error('AI Copy Generator requires Pro or Power plan');
      return;
    }
    
    setLoading(true);
    try {
      const response = await aiAPI.generateCopy(
        copyForm.topic, 
        copyForm.tone, 
        copyForm.category
      );
      // Backend returns {headline, description, cta} directly
      setGeneratedCopy(response.data);
      toast.success('Copy generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate copy');
    } finally {
      setLoading(false);
    }
  };

  const optimizePromotion = async () => {
    if (!isPro) {
      toast.error('AI Optimization requires Pro or Power plan');
      return;
    }
    
    if (!selectedPromo) {
      toast.error('Please select a promotion');
      return;
    }
    
    setLoading(true);
    try {
      const response = await aiAPI.optimize(selectedPromo);
      // Backend returns optimization object directly
      setOptimization(response.data);
      toast.success('Optimization suggestions generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to optimize');
    } finally {
      setLoading(false);
    }
  };

  const generateImage = async () => {
    if (!isPower) {
      toast.error('AI Image Generator requires Power plan');
      return;
    }
    
    setLoading(true);
    try {
      const response = await aiAPI.generateImage(imageForm.prompt, imageForm.style);
      setGeneratedImage(response.data);
      toast.success('Image generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate image');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const LockedOverlay = ({ requiredPlan }) => (
    <div className="absolute inset-0 bg-void/90 backdrop-blur-sm flex flex-col items-center justify-center z-10 rounded-lg">
      <Lock className="w-10 h-10 text-gray-500 mb-4" />
      <p className="text-gray-400 text-center mb-4">
        This feature requires {requiredPlan} plan
      </p>
      <Button 
        className="bg-neon-violet text-white hover:bg-neon-violet/90"
        onClick={() => navigate('/dashboard/billing')}
      >
        <Crown className="w-4 h-4 mr-2" />
        Upgrade to {requiredPlan}
      </Button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ai-tools-page">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl text-white flex items-center gap-2">
          <Sparkles className="w-6 h-6 text-neon-violet" />
          AI Tools
        </h1>
        <p className="text-gray-400">Supercharge your promotions with AI</p>
      </div>

      {/* Plan Banner */}
      {!isPro && (
        <div className="bg-gradient-to-r from-neon-violet/20 to-neon-mint/20 border border-neon-violet/30 rounded-xl p-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="font-heading font-semibold text-lg text-white mb-1">
                Unlock AI Features
              </h3>
              <p className="text-gray-400">
                Upgrade to Pro for AI copy generation and optimization, or Power for AI image generation.
              </p>
            </div>
            <Button 
              className="bg-neon-violet text-white hover:bg-neon-violet/90 font-bold shrink-0"
              onClick={() => navigate('/dashboard/billing')}
            >
              <Crown className="w-4 h-4 mr-2" />
              Upgrade Now
            </Button>
          </div>
        </div>
      )}

      <Tabs defaultValue="copy" className="space-y-6">
        <TabsList className="bg-void-paper border border-white/10">
          <TabsTrigger value="copy" className="data-[state=active]:bg-neon-violet/20 data-[state=active]:text-neon-violet">
            <Wand2 className="w-4 h-4 mr-2" />
            Copy Generator
          </TabsTrigger>
          <TabsTrigger value="optimize" className="data-[state=active]:bg-neon-violet/20 data-[state=active]:text-neon-violet">
            <TrendingUp className="w-4 h-4 mr-2" />
            Optimizer
          </TabsTrigger>
          <TabsTrigger value="image" className="data-[state=active]:bg-neon-violet/20 data-[state=active]:text-neon-violet">
            <Image className="w-4 h-4 mr-2" />
            Image Generator
          </TabsTrigger>
        </TabsList>

        {/* Copy Generator Tab */}
        <TabsContent value="copy">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-void-paper border-white/10 relative overflow-hidden">
              {!isPro && <LockedOverlay requiredPlan="Pro" />}
              <CardHeader>
                <CardTitle className="text-white font-heading flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-neon-violet" />
                  Generate Promo Copy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>What are you promoting?</Label>
                  <Textarea
                    value={copyForm.topic}
                    onChange={(e) => setCopyForm(prev => ({ ...prev, topic: e.target.value }))}
                    placeholder="Describe your product, service, or offer..."
                    rows={3}
                    className="bg-void border-white/10 focus:border-neon-violet resize-none"
                    data-testid="ai-copy-topic"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tone</Label>
                    <Select
                      value={copyForm.tone}
                      onValueChange={(value) => setCopyForm(prev => ({ ...prev, tone: value }))}
                    >
                      <SelectTrigger className="bg-void border-white/10">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-void-paper border-white/10">
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="casual">Casual</SelectItem>
                        <SelectItem value="urgent">Urgent</SelectItem>
                        <SelectItem value="friendly">Friendly</SelectItem>
                        <SelectItem value="bold">Bold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Category</Label>
                    <Select
                      value={copyForm.category}
                      onValueChange={(value) => setCopyForm(prev => ({ ...prev, category: value }))}
                    >
                      <SelectTrigger className="bg-void border-white/10">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-void-paper border-white/10">
                        <SelectItem value="Technology">Technology</SelectItem>
                        <SelectItem value="Business">Business</SelectItem>
                        <SelectItem value="Marketing">Marketing</SelectItem>
                        <SelectItem value="Finance">Finance</SelectItem>
                        <SelectItem value="Health">Health</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Button 
                  onClick={generateCopy}
                  disabled={loading || !copyForm.topic}
                  className="w-full bg-neon-violet text-white hover:bg-neon-violet/90 font-bold"
                  data-testid="generate-copy-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Generate Copy
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-void-paper border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading">Generated Copy</CardTitle>
              </CardHeader>
              <CardContent>
                {generatedCopy ? (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-neon-mint">Headline</Label>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(generatedCopy.headline)}
                          className="text-gray-400 hover:text-white h-8"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <p className="text-white font-medium bg-void p-3 rounded-lg">
                        {generatedCopy.headline}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-neon-mint">Description</Label>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(generatedCopy.description)}
                          className="text-gray-400 hover:text-white h-8"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <p className="text-gray-300 bg-void p-3 rounded-lg">
                        {generatedCopy.description}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-neon-mint">Call to Action</Label>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => copyToClipboard(generatedCopy.cta)}
                          className="text-gray-400 hover:text-white h-8"
                        >
                          <Copy className="w-3 h-3 mr-1" />
                          Copy
                        </Button>
                      </div>
                      <p className="text-white font-bold bg-void p-3 rounded-lg">
                        {generatedCopy.cta}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Wand2 className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500">Generated copy will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Optimizer Tab */}
        <TabsContent value="optimize">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-void-paper border-white/10 relative overflow-hidden">
              {!isPro && <LockedOverlay requiredPlan="Pro" />}
              <CardHeader>
                <CardTitle className="text-white font-heading flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-neon-violet" />
                  Optimize Promotion
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Select a promotion to optimize</Label>
                  <Select value={selectedPromo} onValueChange={setSelectedPromo}>
                    <SelectTrigger className="bg-void border-white/10" data-testid="select-promo-optimize">
                      <SelectValue placeholder="Choose a promotion" />
                    </SelectTrigger>
                    <SelectContent className="bg-void-paper border-white/10">
                      {promotions.map(promo => (
                        <SelectItem key={promo.id} value={promo.id} className="text-white">
                          {promo.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedPromo && (
                  <div className="bg-void rounded-lg p-4">
                    {(() => {
                      const promo = promotions.find(p => p.id === selectedPromo);
                      if (!promo) return null;
                      const ctr = promo.views > 0 ? (promo.clicks / promo.views * 100).toFixed(1) : 0;
                      return (
                        <div className="space-y-2">
                          <p className="text-white font-medium">{promo.title}</p>
                          <p className="text-gray-400 text-sm">{promo.description}</p>
                          <div className="flex gap-4 text-sm">
                            <span className="text-gray-500">{promo.views} views</span>
                            <span className="text-gray-500">{promo.clicks} clicks</span>
                            <span className="text-neon-mint">{ctr}% CTR</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
                
                <Button 
                  onClick={optimizePromotion}
                  disabled={loading || !selectedPromo}
                  className="w-full bg-neon-violet text-white hover:bg-neon-violet/90 font-bold"
                  data-testid="optimize-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Get AI Suggestions
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-void-paper border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading">Optimization Results</CardTitle>
              </CardHeader>
              <CardContent>
                {optimization ? (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4 p-4 bg-void rounded-lg">
                      <div className="w-16 h-16 rounded-full bg-gradient-to-br from-neon-mint to-neon-violet flex items-center justify-center">
                        <span className="font-mono font-bold text-2xl text-white">
                          {optimization.score}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-medium">AI Score</p>
                        <p className="text-gray-400 text-sm">Based on copy quality and engagement</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label className="text-neon-amber">Suggestions</Label>
                      <ul className="space-y-2">
                        {optimization.suggestions?.map((suggestion, i) => (
                          <li key={i} className="flex items-start gap-2 text-gray-300 bg-void p-3 rounded-lg">
                            <Zap className="w-4 h-4 text-neon-amber mt-0.5 shrink-0" />
                            {suggestion}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {optimization.new_headline && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-neon-mint">Suggested Headline</Label>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(optimization.new_headline)}
                            className="text-gray-400 hover:text-white h-8"
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy
                          </Button>
                        </div>
                        <p className="text-white bg-void p-3 rounded-lg">
                          {optimization.new_headline}
                        </p>
                      </div>
                    )}
                    
                    {optimization.new_description && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-neon-mint">Suggested Description</Label>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => copyToClipboard(optimization.new_description)}
                            className="text-gray-400 hover:text-white h-8"
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy
                          </Button>
                        </div>
                        <p className="text-gray-300 bg-void p-3 rounded-lg">
                          {optimization.new_description}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <TrendingUp className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500">Optimization suggestions will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Image Generator Tab */}
        <TabsContent value="image">
          <div className="grid lg:grid-cols-2 gap-6">
            <Card className="bg-void-paper border-white/10 relative overflow-hidden">
              {!isPower && <LockedOverlay requiredPlan="Power" />}
              <CardHeader>
                <CardTitle className="text-white font-heading flex items-center gap-2">
                  <Image className="w-5 h-5 text-neon-violet" />
                  Generate Promo Image
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Describe your image</Label>
                  <Textarea
                    value={imageForm.prompt}
                    onChange={(e) => setImageForm(prev => ({ ...prev, prompt: e.target.value }))}
                    placeholder="A professional banner for a SaaS product showing growth charts and happy customers..."
                    rows={4}
                    className="bg-void border-white/10 focus:border-neon-violet resize-none"
                    data-testid="ai-image-prompt"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Style</Label>
                  <Select
                    value={imageForm.style}
                    onValueChange={(value) => setImageForm(prev => ({ ...prev, style: value }))}
                  >
                    <SelectTrigger className="bg-void border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-void-paper border-white/10">
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="modern">Modern</SelectItem>
                      <SelectItem value="minimalist">Minimalist</SelectItem>
                      <SelectItem value="vibrant">Vibrant</SelectItem>
                      <SelectItem value="corporate">Corporate</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <Button 
                  onClick={generateImage}
                  disabled={loading || !imageForm.prompt}
                  className="w-full bg-neon-violet text-white hover:bg-neon-violet/90 font-bold"
                  data-testid="generate-image-btn"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Generate Image
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-void-paper border-white/10">
              <CardHeader>
                <CardTitle className="text-white font-heading">Generated Image</CardTitle>
              </CardHeader>
              <CardContent>
                {generatedImage?.full_image ? (
                  <div className="space-y-4">
                    <div className="rounded-lg overflow-hidden bg-void">
                      <img 
                        src={`data:${generatedImage.mime_type};base64,${generatedImage.full_image}`}
                        alt="Generated promo"
                        className="w-full h-auto"
                      />
                    </div>
                    {generatedImage.text_response && (
                      <p className="text-gray-400 text-sm">{generatedImage.text_response}</p>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Image className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <p className="text-gray-500">Generated image will appear here</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AITools;
